import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, LogOut, FileText, CheckCircle, XCircle, Clock,
  User, Download, Eye, AlertTriangle, RefreshCw, Shield,
  Image, ExternalLink, ChevronDown, Archive
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BACKEND_URL } from '../config/api';

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  verified: { label: 'Verified', icon: CheckCircle, className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  rejected: { label: 'Rejected', icon: XCircle, className: 'bg-red-500/20 text-red-400 border-red-500/30' },
  expired: { label: 'Expired', icon: AlertTriangle, className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  needs_resubmission: { label: 'Needs Resubmission', icon: RefreshCw, className: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${cfg.className}`} data-testid={`status-badge-${status}`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </span>
  );
}

function AttachmentPreview({ attachment, compact }) {
  if (!attachment || !attachment.file_url) return null;
  const isImage = attachment.file_type?.startsWith('image/') || /\.(jpg|jpeg|png|webp|gif)$/i.test(attachment.file_url);
  const isPdf = attachment.file_type === 'application/pdf' || /\.pdf$/i.test(attachment.file_url);

  if (compact) {
    return (
      <a href={attachment.file_url} target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-orange-400 hover:text-orange-300 text-sm transition"
        data-testid={`attachment-link-${attachment.label}`}>
        {isImage ? <Image className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
        {attachment.file_name || attachment.label}
        <ExternalLink className="h-3 w-3" />
      </a>
    );
  }

  return (
    <div className="space-y-2" data-testid={`attachment-preview-${attachment.label}`}>
      <p className="text-xs text-gray-400 uppercase tracking-wide">{attachment.label?.replace(/_/g, ' ')}</p>
      {isImage ? (
        <div>
          <a href={attachment.file_url} target="_blank" rel="noopener noreferrer" className="block">
            <img
              src={attachment.file_url}
              alt={attachment.label}
              className="max-w-full max-h-64 rounded-lg border border-gray-700 object-contain bg-black/30"
              onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.nextSibling.style.display = 'flex'; }}
            />
          </a>
          <div className="hidden items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
            <AlertTriangle className="h-4 w-4" /> Image failed to load
            <a href={attachment.file_url} target="_blank" rel="noopener noreferrer" className="ml-auto text-orange-400 hover:underline">
              Open directly <ExternalLink className="h-3 w-3 inline" />
            </a>
          </div>
        </div>
      ) : isPdf ? (
        <div className="flex items-center gap-3 p-3 bg-[#0a0a0f] border border-gray-700 rounded-lg">
          <FileText className="h-8 w-8 text-red-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm truncate">{attachment.file_name || 'PDF Document'}</p>
            <p className="text-gray-500 text-xs">PDF file</p>
          </div>
          <a href={attachment.file_url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 px-3 py-1.5 bg-orange-500/20 text-orange-400 rounded-lg text-sm hover:bg-orange-500/30 transition">
            <Download className="h-3.5 w-3.5" /> Open
          </a>
        </div>
      ) : (
        <a href={attachment.file_url} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 p-3 bg-[#0a0a0f] border border-gray-700 rounded-lg text-orange-400 hover:bg-gray-800 transition text-sm">
          <Download className="h-4 w-4" /> Download: {attachment.file_name || 'File'}
        </a>
      )}
    </div>
  );
}

function MissingAttachmentWarning({ label }) {
  return (
    <div className="flex items-center gap-2 p-2.5 bg-red-500/10 border border-red-500/20 rounded-lg" data-testid={`missing-attachment-${label}`}>
      <AlertTriangle className="h-4 w-4 text-red-400 flex-shrink-0" />
      <span className="text-red-400 text-sm">{label?.replace(/_/g, ' ')} - Missing</span>
    </div>
  );
}

export default function AdminVerificationQueue() {
  const navigate = useNavigate();
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [typeConfigs, setTypeConfigs] = useState({});
  const [rejectionReasons, setRejectionReasons] = useState({});

  // Review modal
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('');
  const [reviewRejectionReason, setReviewRejectionReason] = useState('');
  const [reviewAdminNotes, setReviewAdminNotes] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      if (response.data.user_type !== 'admin') { navigate('/login'); return; }
      setCurrentAdmin(response.data);
    } catch { navigate('/login'); }
  }, [navigate]);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/credential-types-config`, { withCredentials: true });
      setTypeConfigs(res.data.configs || {});
      setRejectionReasons(res.data.rejection_reasons || {});
    } catch { /* config is optional for display */ }
  }, []);

  const fetchCredentials = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/verification-queue`, {
        params: { status: statusFilter !== 'all' ? statusFilter : undefined },
        withCredentials: true,
      });
      setCredentials(response.data);
    } catch {
      toast.error('Failed to fetch verification queue');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { checkAuth(); fetchConfig(); }, [checkAuth, fetchConfig]);
  useEffect(() => { if (currentAdmin) fetchCredentials(); }, [currentAdmin, statusFilter, fetchCredentials]);

  const openReviewModal = (cred) => {
    setSelectedCredential(cred);
    setReviewStatus('');
    setReviewRejectionReason('');
    setReviewAdminNotes('');
    setShowReviewModal(true);
  };

  const handleSubmitReview = async () => {
    if (!reviewStatus) { toast.error('Select a decision'); return; }
    if (reviewStatus === 'rejected' && !reviewRejectionReason) { toast.error('Select a rejection reason'); return; }

    setSubmittingReview(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/credentials/${selectedCredential.credential_id}/review`,
        {
          status: reviewStatus,
          rejection_reason: reviewStatus === 'rejected' ? reviewRejectionReason : undefined,
          admin_notes: reviewAdminNotes || undefined,
        },
        { withCredentials: true }
      );
      toast.success(`Credential ${reviewStatus === 'verified' ? 'verified' : reviewStatus === 'rejected' ? 'rejected' : reviewStatus.replace(/_/g, ' ')}`);
      setShowReviewModal(false);
      fetchCredentials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Review failed');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleLogout = async () => {
    try { await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true }); } catch {}
    navigate('/login');
  };

  // Check if a credential is legacy (has legacy_data, or missing new schema fields)
  const isLegacy = (cred) => cred.legacy_data != null || !cred.credential_category;

  // Get display value for a field key from credential
  const getFieldValue = (cred, fieldKey) => {
    const fields = cred.fields || {};
    const val = fields[fieldKey];
    if (val !== undefined && val !== null && val !== '') return val;
    // Fallback to top-level legacy fields
    if (fieldKey === 'card_number' || fieldKey === 'certificate_reference') return cred.credential_number || null;
    if (fieldKey === 'issuing_body' || fieldKey === 'awarding_body') return cred.issuing_body || null;
    if (fieldKey === 'expiry_date') return cred.expiry_date || null;
    return null;
  };

  // Build detail fields for review modal
  const getDetailFields = (cred) => {
    const config = typeConfigs[cred.credential_type];
    if (!config) {
      // Unknown type — show whatever we have
      const fields = cred.fields || {};
      const entries = Object.entries(fields).map(([k, v]) => ({ label: k.replace(/_/g, ' '), value: v }));
      // Add legacy top-level fields
      if (cred.credential_number) entries.push({ label: 'Credential Number', value: cred.credential_number });
      if (cred.issuing_body) entries.push({ label: 'Issuing Body', value: cred.issuing_body });
      if (cred.expiry_date) entries.push({ label: 'Expiry Date', value: cred.expiry_date });
      return entries;
    }
    const allFieldDefs = [...config.required_fields, ...config.optional_fields];
    const detailKeys = config.admin_detail_fields || allFieldDefs.map(f => f.key);
    return detailKeys.map(key => {
      const def = allFieldDefs.find(f => f.key === key);
      const value = getFieldValue(cred, key);
      return { label: def?.label || key.replace(/_/g, ' '), value };
    });
  };

  // Get attachments with expected vs actual
  const getAttachmentStatus = (cred) => {
    const config = typeConfigs[cred.credential_type];
    const actual = cred.attachments || [];
    const actualLabels = new Set(actual.map(a => a.label));

    const required = config?.required_attachments || [];
    const optional = config?.optional_attachments || [];

    const result = [];
    for (const att of required) {
      const found = actual.find(a => a.label === att.key);
      result.push({ ...att, required: true, attachment: found || null });
    }
    for (const att of optional) {
      const found = actual.find(a => a.label === att.key);
      if (found) result.push({ ...att, required: false, attachment: found });
    }
    // Include any unlabeled attachments from legacy data
    for (const att of actual) {
      if (!required.some(r => r.key === att.label) && !optional.some(o => o.key === att.label)) {
        result.push({ key: att.label, label: att.label || 'Document', required: false, attachment: att });
      }
    }
    // Legacy document_url
    if (cred.document_url && !actual.length) {
      result.push({
        key: 'legacy_document', label: 'Uploaded Document', required: false,
        attachment: { label: 'legacy_document', file_url: cred.document_url, file_type: 'unknown', file_name: 'Legacy upload' }
      });
    }
    return result;
  };

  const filterTabs = [
    { value: 'pending', label: 'Pending' },
    { value: 'verified', label: 'Verified' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'needs_resubmission', label: 'Resubmission' },
    { value: 'expired', label: 'Expired' },
    { value: 'all', label: 'All' },
  ];

  const counts = {};
  if (statusFilter === 'all') {
    for (const tab of filterTabs) {
      if (tab.value === 'all') { counts.all = credentials.length; }
      else { counts[tab.value] = credentials.filter(c => c.verification_status === tab.value).length; }
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Header */}
      <header className="bg-[#12121a] border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 text-orange-500" data-testid="logo" />
              <span className="text-xl font-bold text-white">Credential Verification</span>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin/dashboard')} className="text-gray-400 hover:text-white text-sm" data-testid="nav-dashboard">
                Dashboard
              </button>
              <button onClick={() => navigate('/admin/user-control')} className="text-gray-400 hover:text-white text-sm" data-testid="nav-user-control">
                User Control
              </button>
              <span className="text-sm text-gray-500" data-testid="admin-name">{currentAdmin?.name}</span>
              <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-1.5 text-gray-400 hover:text-white transition" data-testid="logout-btn">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="filter-tabs">
          {filterTabs.map(tab => (
            <button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                statusFilter === tab.value
                  ? 'bg-orange-500 text-white'
                  : 'bg-[#12121a] border border-gray-700 text-gray-400 hover:text-white hover:border-gray-600'
              }`}
              data-testid={`filter-${tab.value}`}
            >
              {tab.label}
              {counts[tab.value] !== undefined && <span className="ml-1.5 opacity-70">({counts[tab.value]})</span>}
            </button>
          ))}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#12121a] border border-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg"><Clock className="w-5 h-5 text-yellow-400" /></div>
              <div>
                <p className="text-gray-400 text-sm">Showing</p>
                <p className="text-2xl font-bold text-white" data-testid="showing-count">{credentials.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-[#12121a] border border-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg"><Shield className="w-5 h-5 text-blue-400" /></div>
              <div>
                <p className="text-gray-400 text-sm">Filter</p>
                <p className="text-lg font-bold text-white capitalize">{statusFilter}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Credentials List */}
        <div className="bg-[#12121a] border border-gray-800 rounded-xl overflow-hidden" data-testid="credentials-list">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
            </div>
          ) : credentials.length > 0 ? (
            <div className="divide-y divide-gray-800">
              {credentials.map((cred) => {
                const config = typeConfigs[cred.credential_type];
                const typeLabel = cred.type_label || config?.label || cred.credential_type || 'Unknown';
                const numberLabel = cred.number_label || config?.number_label || 'Number';
                const numberField = config?.number_label === 'Card Number'
                  ? getFieldValue(cred, 'card_number')
                  : getFieldValue(cred, 'certificate_reference') || getFieldValue(cred, 'card_number');
                const credNumber = numberField || cred.credential_number || 'Not provided';
                const status = cred.verification_status || 'pending';
                const legacy = isLegacy(cred);
                const summaryEntries = Object.entries(cred.admin_summary || {});

                return (
                  <div key={cred.credential_id} className="p-4 hover:bg-[#0a0a0f]/50 transition" data-testid={`credential-row-${cred.credential_id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Header row */}
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <StatusBadge status={status} />
                          <span className="text-white font-semibold text-sm">{typeLabel}</span>
                          {legacy && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-700 text-gray-300 border border-gray-600" data-testid={`legacy-badge-${cred.credential_id}`}>
                              <Archive className="h-2.5 w-2.5" /> Legacy
                            </span>
                          )}
                          <span className="text-gray-600 text-xs ml-auto">
                            {cred.submitted_at ? new Date(cred.submitted_at).toLocaleDateString() : ''}
                          </span>
                        </div>

                        {/* User info */}
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-white" />
                          </div>
                          <div className="min-w-0">
                            <button
                              onClick={() => navigate(`/admin/user/${cred.user_id}`)}
                              className="text-white text-sm font-medium hover:text-orange-400 transition truncate block"
                              data-testid={`user-link-${cred.credential_id}`}
                            >
                              {cred.user?.name || 'Unknown User'}
                            </button>
                            <p className="text-gray-500 text-xs truncate">{cred.user?.email}</p>
                          </div>
                        </div>

                        {/* Summary grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-[#0a0a0f] rounded-lg text-sm">
                          <div>
                            <p className="text-gray-500 text-xs">{numberLabel}</p>
                            <p className="text-white truncate">{credNumber}</p>
                          </div>
                          {summaryEntries.map(([label, value]) => (
                            <div key={label}>
                              <p className="text-gray-500 text-xs">{label}</p>
                              <p className="text-white truncate">{value}</p>
                            </div>
                          ))}
                          {cred.profile?.trade && (
                            <div>
                              <p className="text-gray-500 text-xs">Trade</p>
                              <p className="text-white truncate">{cred.profile.trade}</p>
                            </div>
                          )}
                        </div>

                        {/* Attachments indicator */}
                        {(cred.has_attachments || cred.attachments?.length > 0) && (
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            {(cred.attachments || []).map(att => (
                              <AttachmentPreview key={att.attachment_id || att.label} attachment={att} compact />
                            ))}
                          </div>
                        )}
                        {cred.document_url && !cred.attachments?.length && (
                          <div className="mt-2">
                            <a href={cred.document_url} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-orange-400 hover:text-orange-300 text-sm">
                              <FileText className="h-3.5 w-3.5" /> Legacy Document <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        )}

                        {/* Rejection info */}
                        {cred.rejection_reason && (
                          <div className="mt-2 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
                            <p className="text-red-400 text-xs">
                              Rejection: {rejectionReasons[cred.rejection_reason] || cred.rejection_reason.replace(/_/g, ' ')}
                            </p>
                          </div>
                        )}
                        {cred.admin_notes && (
                          <div className="mt-2 p-2 bg-gray-800/50 border border-gray-700 rounded-lg">
                            <p className="text-gray-400 text-xs">Notes: {cred.admin_notes}</p>
                          </div>
                        )}
                      </div>

                      {/* Review button */}
                      <button
                        onClick={() => openReviewModal(cred)}
                        className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition text-sm font-medium flex-shrink-0"
                        data-testid={`review-btn-${cred.credential_id}`}
                      >
                        <Eye className="w-3.5 h-3.5" /> Review
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16" data-testid="empty-state">
              <CheckCircle className="w-12 h-12 text-green-500/30 mx-auto mb-3" />
              <p className="text-gray-500">No credentials for this filter</p>
            </div>
          )}
        </div>
      </main>

      {/* ═══════════ REVIEW MODAL ═══════════ */}
      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="bg-[#12121a] border border-gray-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Review Credential
              {selectedCredential && isLegacy(selectedCredential) && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-700 text-gray-300 border border-gray-600">
                  <Archive className="h-2.5 w-2.5" /> Legacy submission
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedCredential && (() => {
            const cred = selectedCredential;
            const config = typeConfigs[cred.credential_type];
            const typeLabel = cred.type_label || config?.label || cred.credential_type || 'Unknown';
            const legacy = isLegacy(cred);
            const detailFields = getDetailFields(cred);
            const attachmentStatus = getAttachmentStatus(cred);
            const status = cred.verification_status || 'pending';

            return (
              <div className="space-y-5 mt-2" data-testid="review-modal-content">
                {/* Type & Status */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">{typeLabel}</h3>
                    <p className="text-gray-500 text-sm">
                      {cred.credential_type} | {cred.credential_category || 'uncategorized'}
                    </p>
                  </div>
                  <StatusBadge status={status} />
                </div>

                {/* Tradesperson Info */}
                <div className="p-3 bg-[#0a0a0f] rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-medium" data-testid="review-user-name">{cred.user?.name || 'Unknown'}</p>
                      <p className="text-gray-500 text-sm">{cred.user?.email} | Trade: {cred.profile?.trade || 'Not set'}</p>
                    </div>
                  </div>
                </div>

                {/* Detail Fields */}
                <div className="space-y-1">
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Credential Details</p>
                  <div className="grid grid-cols-2 gap-3" data-testid="review-detail-fields">
                    {detailFields.map(({ label, value }) => (
                      <div key={label} className="p-2 bg-[#0a0a0f] rounded-lg">
                        <p className="text-gray-500 text-xs">{label}</p>
                        <p className={`text-sm ${value ? 'text-white' : 'text-gray-600 italic'}`}>
                          {value || 'Not provided'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Attachments */}
                <div className="space-y-3">
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Attachments</p>
                  {attachmentStatus.length > 0 ? (
                    attachmentStatus.map(att => (
                      <div key={att.key}>
                        {att.attachment ? (
                          <AttachmentPreview attachment={att.attachment} />
                        ) : att.required ? (
                          <MissingAttachmentWarning label={att.label} />
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg" data-testid="no-attachments-warning">
                      <AlertTriangle className="h-4 w-4 text-yellow-400" />
                      <span className="text-yellow-400 text-sm">No attachments found</span>
                    </div>
                  )}
                </div>

                {/* Previous admin notes / rejection */}
                {(cred.admin_notes || cred.rejection_reason) && (
                  <div className="space-y-2 p-3 bg-gray-800/50 border border-gray-700 rounded-lg">
                    {cred.rejection_reason && (
                      <p className="text-red-400 text-sm">
                        Previous rejection: {rejectionReasons[cred.rejection_reason] || cred.rejection_reason.replace(/_/g, ' ')}
                      </p>
                    )}
                    {cred.admin_notes && <p className="text-gray-400 text-sm">Notes: {cred.admin_notes}</p>}
                  </div>
                )}

                <hr className="border-gray-800" />

                {/* ──── Decision Section ──── */}
                <div className="space-y-4">
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Decision</p>

                  {/* Status buttons */}
                  <div className="grid grid-cols-3 gap-2" data-testid="decision-buttons">
                    <button
                      onClick={() => { setReviewStatus('verified'); setReviewRejectionReason(''); }}
                      className={`flex items-center justify-center gap-2 p-3 rounded-lg border transition text-sm ${
                        reviewStatus === 'verified'
                          ? 'bg-green-500/20 border-green-500/50 text-green-400'
                          : 'bg-[#0a0a0f] border-gray-700 text-gray-400 hover:border-gray-600'
                      }`}
                      data-testid="decision-verified"
                    >
                      <CheckCircle className="w-4 h-4" /> Verify
                    </button>
                    <button
                      onClick={() => setReviewStatus('rejected')}
                      className={`flex items-center justify-center gap-2 p-3 rounded-lg border transition text-sm ${
                        reviewStatus === 'rejected'
                          ? 'bg-red-500/20 border-red-500/50 text-red-400'
                          : 'bg-[#0a0a0f] border-gray-700 text-gray-400 hover:border-gray-600'
                      }`}
                      data-testid="decision-rejected"
                    >
                      <XCircle className="w-4 h-4" /> Reject
                    </button>
                    <button
                      onClick={() => { setReviewStatus('needs_resubmission'); setReviewRejectionReason(''); }}
                      className={`flex items-center justify-center gap-2 p-3 rounded-lg border transition text-sm ${
                        reviewStatus === 'needs_resubmission'
                          ? 'bg-orange-500/20 border-orange-500/50 text-orange-400'
                          : 'bg-[#0a0a0f] border-gray-700 text-gray-400 hover:border-gray-600'
                      }`}
                      data-testid="decision-needs-resubmission"
                    >
                      <RefreshCw className="w-4 h-4" /> Resubmit
                    </button>
                  </div>

                  {/* Granular rejection reasons */}
                  {reviewStatus === 'rejected' && (
                    <div className="space-y-2" data-testid="rejection-reasons">
                      <p className="text-sm text-gray-400">Rejection Reason <span className="text-red-400">*</span></p>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(rejectionReasons).map(([key, description]) => (
                          <button
                            key={key}
                            onClick={() => setReviewRejectionReason(key)}
                            className={`p-2 text-left rounded-lg border transition text-xs ${
                              reviewRejectionReason === key
                                ? 'bg-red-500/20 border-red-500/40 text-red-300'
                                : 'bg-[#0a0a0f] border-gray-700 text-gray-400 hover:border-gray-600'
                            }`}
                            data-testid={`rejection-reason-${key}`}
                          >
                            {description}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Verify confirmation */}
                  {reviewStatus === 'verified' && (
                    <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg" data-testid="verify-confirmation">
                      <p className="text-green-400 text-sm flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        This will verify the credential and add a verification badge to the tradesperson's profile.
                      </p>
                    </div>
                  )}

                  {/* Admin notes */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Admin Notes (optional)</label>
                    <textarea
                      value={reviewAdminNotes}
                      onChange={(e) => setReviewAdminNotes(e.target.value)}
                      rows={2}
                      className="w-full bg-[#0a0a0f] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none"
                      placeholder="Add notes visible to the admin team..."
                      data-testid="review-admin-notes"
                    />
                  </div>

                  {/* Submit */}
                  <button
                    onClick={handleSubmitReview}
                    disabled={!reviewStatus || submittingReview}
                    className="w-full py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                    data-testid="submit-review-btn"
                  >
                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                  </button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
